/**
 * CapturePage — der ganze Scan unter /erfassen (Scanner 0.7, Oktober 2026).
 *
 * Ablauf wie bei Lexware Scan — erst scannen, dann prüfen, dann zuordnen:
 *   kamera       öffnet sofort; jede Aufnahme erscheint kurz groß und fliegt
 *                in den Stapel. Die Kamera bleibt an, auch unter der Übersicht.
 *   uebersicht   „Seite 1 von n": Anpassen · Filter · Drehen · Wiederholen ·
 *                Löschen. Die Seiten werden im Hintergrund fertig gerechnet.
 *   zuordnen     „Was ist das?" + Projekt (eigene Baustelle vorausgewählt),
 *                „Sichern".
 *   verarbeiten  hochladen (1–3 s). Clara liest danach im Hintergrund auf dem
 *                Server — niemand wartet mehr auf sie (16.10.2026).
 *   gesichert    „Neuen Beleg scannen" oder „Zur Liste".
 *
 * Bis 10/2026 kam die Zuordnung VOR der Kamera, jede Seite hatte einen eigenen
 * Prüfschritt, und die Kamera startete für jede Seite neu.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Loader2, X } from "lucide-react";
import ScanKamera from "../components/scan/ScanKamera";
import ScanUebersicht from "../components/scan/ScanUebersicht";
import EckenEditor from "../components/scan/EckenEditor";
import { rendere, type Drehung } from "../lib/scan/render";
import type { Ecken, Filter } from "../lib/scan/bild";
import {
  SCHAERFE_MIN,
  erkenne,
  schaerfeIn,
  standardEcken,
  vorschau,
} from "../lib/scan/aufnahme";
import {
  MAX_SEITEN,
  SCANNER_VERSION,
  neueId,
  type CaptureMeta,
  type NeueSeite,
  type ScanSeite,
} from "../lib/scan/seite";
import { api } from "../lib/api";
import Zuordnung, { type CaptureContext, type Cat, type Wahl } from "./ContextSelectPage";
import "../components/scan/scan.css";

const APP_VERSION = `scanflow-pwa@${SCANNER_VERSION}`;

type Schritt = "kamera" | "uebersicht" | "zuordnen" | "verarbeiten" | "gesichert";

export default function CapturePage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const loc = useLocation();
  const anfangsKat: Cat | undefined =
    (loc.state as { cat?: Cat } | null)?.cat === "privat" ? "privat" : undefined;

  const [schritt, setSchritt] = useState<Schritt>("kamera");
  const [seiten, setSeiten] = useState<ScanSeite[]>([]);
  const seitenRef = useRef(seiten);
  seitenRef.current = seiten;
  const [index, setIndex] = useState(0);
  const ersetzeRef = useRef<string | null>(null);
  const [ersetzen, setErsetzen] = useState(false);
  const [editorId, setEditorId] = useState<string | null>(null);
  const [pdf, setPdf] = useState<File | null>(null);
  const [verwerfenFrage, setVerwerfenFrage] = useState(false);
  const [hinweis, setHinweis] = useState<string | null>(null);
  // Die Zuordnung des letzten Versuchs — nach einem Fehler beim Hochladen steht
  // sie wieder da (03.11.2026: ein Bewirtungsbeleg kam nach dem Abbruch als
  // „Rechnung" zurück und wurde so gesichert).
  const [letzteWahl, setLetzteWahl] = useState<Wahl | null>(null);
  const videoMassRef = useRef({ w: 0, h: 0 });

  // Verarbeitung
  const [statusMsg, setStatusMsg] = useState("");
  const [gesichert, setGesichert] = useState<{
    art: "beleg" | "lieferschein" | "privat";
    bild: string | null;
  } | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewAspect, setPreviewAspect] = useState<number | null>(null);
  const savedBelegIdRef = useRef<string | null>(null);
  // Nummer des bereits angelegten privaten Dokuments. Anders als beim Beleg wird
  // es beim Abbrechen NICHT gelöscht: die Datei liegt schon in der Cloud.
  const privatDokIdRef = useRef<string | null>(null);
  // „Abbrechen" während Hochladen oder Auswertung: der laufende Ablauf darf
  // danach nichts mehr tun (08.10.2026).
  const abgebrochenRef = useRef(false);

  useKoerperSperre();

  const melde = useCallback((t: string, ms = 3500) => {
    setHinweis(t);
    window.setTimeout(() => setHinweis((h) => (h === t ? null : h)), ms);
  }, []);

  // Beim Verlassen alle Blob-Adressen freigeben
  useEffect(
    () => () => {
      seitenRef.current.forEach((s) => {
        URL.revokeObjectURL(s.vorschau);
        if (s.ergebnis) URL.revokeObjectURL(s.ergebnis.url);
      });
    },
    [],
  );

  // ── Seiten fertig rechnen (im Hintergrund) ──
  const rechne = useCallback((s: ScanSeite) => {
    const stand = s.stand;
    rendere({ quelle: s.quelle, ecken: s.ecken, drehung: s.drehung, filter: s.filter })
      .then((erg) => {
        const jetzt = seitenRef.current.find((x) => x.id === s.id);
        if (!jetzt || jetzt.stand !== stand) return; // inzwischen geändert/gelöscht
        const url = URL.createObjectURL(erg.blob);
        setSeiten((l) =>
          l.map((x) =>
            x.id === s.id && x.stand === stand ? { ...x, ergebnis: { ...erg, url }, fehler: undefined } : x,
          ),
        );
      })
      .catch((err) => {
        console.warn("Seite aufbereiten:", err);
        setSeiten((l) =>
          l.map((x) =>
            x.id === s.id && x.stand === stand ? { ...x, fehler: String(err?.message || err) } : x,
          ),
        );
      });
  }, []);

  const aendere = useCallback(
    (id: string, patch: { ecken?: Ecken; drehung?: Drehung; filter?: Filter }) => {
      const s = seitenRef.current.find((x) => x.id === id);
      if (!s) return;
      if (s.ergebnis) URL.revokeObjectURL(s.ergebnis.url);
      const neu: ScanSeite = { ...s, ...patch, ergebnis: null, fehler: undefined, stand: s.stand + 1 };
      setSeiten((l) => l.map((x) => (x.id === id ? neu : x)));
      seitenRef.current = seitenRef.current.map((x) => (x.id === id ? neu : x));
      rechne(neu);
    },
    [rechne],
  );

  const neueSeite = useCallback(
    (n: NeueSeite) => {
      const s: ScanSeite = { ...n, id: neueId(), drehung: 0, filter: "dokument", ergebnis: null, stand: 0 };
      const ersetzeId = ersetzeRef.current;
      const alt = ersetzeId ? seitenRef.current.find((x) => x.id === ersetzeId) : undefined;
      if (alt) {
        const pos = seitenRef.current.indexOf(alt);
        seitenRef.current = seitenRef.current.map((x) => (x.id === alt.id ? s : x));
        setSeiten(seitenRef.current);
        URL.revokeObjectURL(alt.vorschau);
        if (alt.ergebnis) URL.revokeObjectURL(alt.ergebnis.url);
        ersetzeRef.current = null;
        setErsetzen(false);
        setIndex(pos);
        setSchritt("uebersicht");
      } else {
        seitenRef.current = [...seitenRef.current, s];
        setSeiten(seitenRef.current);
        setIndex(seitenRef.current.length - 1);
      }
      rechne(s);
    },
    [rechne],
  );

  // „Wählen": Fotos oder ein PDF vom Telefon
  const dateien = useCallback(
    async (files: File[]) => {
      const istPdf = (f: File) => f.type === "application/pdf" || /\.pdf$/i.test(f.name);
      const pdfs = files.filter(istPdf);
      const bilder = files.filter((f) => !istPdf(f));
      if (pdfs.length) {
        if (pdfs.length > 1 || bilder.length || seitenRef.current.length) {
          melde("Ein PDF bitte einzeln wählen — ohne weitere Seiten.");
          return;
        }
        setPdf(pdfs[0]);
        setSchritt("zuordnen");
        return;
      }
      let neu = 0;
      for (const f of bilder) {
        if (seitenRef.current.length >= MAX_SEITEN && !ersetzeRef.current) {
          melde(`Höchstens ${MAX_SEITEN} Seiten je Beleg.`);
          break;
        }
        try {
          const bmp = await createImageBitmap(f);
          const r = erkenne(bmp);
          const ecken = r?.ecken ?? standardEcken(bmp.width, bmp.height);
          const sh = schaerfeIn(bmp, r?.ecken ?? null);
          const url = await vorschau(bmp, ecken);
          const w = bmp.width, h = bmp.height;
          bmp.close?.();
          neueSeite({
            quelle: f,
            w,
            h,
            ecken,
            erkannt: !!r,
            unscharf: sh < SCHAERFE_MIN,
            vorschau: url,
            meta: {
              w,
              h,
              sharp: Math.round(sh * 100) / 100,
              detected: !!r,
              areaFrac: Math.round((r?.flaeche ?? 1) * 1000) / 1000,
              mode: "datei",
              v: 2,
            },
          });
          neu++;
        } catch (err) {
          console.warn("Datei:", err);
          melde(`„${f.name}" ließ sich nicht öffnen.`);
        }
      }
      if (neu) setSchritt("uebersicht");
    },
    [melde, neueSeite],
  );

  const loesche = (id: string) => {
    const s = seitenRef.current.find((x) => x.id === id);
    if (!s) return;
    URL.revokeObjectURL(s.vorschau);
    if (s.ergebnis) URL.revokeObjectURL(s.ergebnis.url);
    const rest = seitenRef.current.filter((x) => x.id !== id);
    seitenRef.current = rest;
    setSeiten(rest);
    setIndex((i) => Math.max(0, Math.min(i, rest.length - 1)));
    if (!rest.length) setSchritt("kamera");
  };

  const zurKamera = () => {
    ersetzeRef.current = null;
    setErsetzen(false);
    setSchritt("kamera");
  };

  const verwerfen = () => {
    setVerwerfenFrage(false);
    nav("/inbox", { replace: true });
  };

  // Zurück-Geste des Telefons: einen Schritt zurück statt die App zu verlassen.
  useZurueckWache(seiten.length > 0 || schritt !== "kamera", () => {
    if (editorId) {
      setEditorId(null);
      return true;
    }
    if (verwerfenFrage) {
      setVerwerfenFrage(false);
      return true;
    }
    if (schritt === "verarbeiten") return true;
    if (schritt === "gesichert") return false;
    if (schritt === "zuordnen") {
      if (pdf) {
        setPdf(null);
        setSchritt("kamera");
      } else setSchritt("uebersicht");
      return true;
    }
    if (schritt === "uebersicht") {
      zurKamera();
      return true;
    }
    if (seitenRef.current.length) {
      setVerwerfenFrage(true);
      return true;
    }
    return false;
  });

  // ── Hochladen (Clara liest danach im Hintergrund) ──
  async function verarbeiten(ctx: CaptureContext, wahl?: Wahl) {
    if (wahl) setLetzteWahl(wahl);
    abgebrochenRef.current = false;
    savedBelegIdRef.current = null;
    privatDokIdRef.current = null;
    setSchritt("verarbeiten");
    setPreviewAspect(null);
    try {
      let blobs: Blob[];
      let captureMeta: CaptureMeta | undefined;
      if (pdf) {
        blobs = [pdf];
        setPreviewUrl(null);
      } else {
        const liste = seitenRef.current;
        setPreviewUrl(liste[0]?.ergebnis?.url ?? liste[0]?.vorschau ?? null);
        setStatusMsg(liste.length > 1 ? "Seiten werden aufbereitet…" : "Seite wird aufbereitet…");
        blobs = [];
        for (const s of liste) {
          if (s.ergebnis) blobs.push(s.ergebnis.blob);
          else {
            const e = await rendere({ quelle: s.quelle, ecken: s.ecken, drehung: s.drehung, filter: s.filter });
            blobs.push(e.blob);
          }
          if (abgebrochenRef.current) return;
        }
        captureMeta = {
          video: { ...videoMassRef.current },
          scanner: SCANNER_VERSION,
          pages: liste.map((s) => s.meta),
        };
      }
      setStatusMsg(blobs.length > 1 ? `${blobs.length} Seiten werden hochgeladen…` : "Beleg wird hochgeladen…");

      // Privater Zweig — eigene Pipeline, keine Buchung/DATEV.
      if (ctx.privat) {
        setStatusMsg("Privates Dokument wird gespeichert…");
        // Wie bei Belegen: Clara liest auf dem Server im Hintergrund.
        const dok = await api.uploadPrivat(blobs, { app_version: APP_VERSION, auswerten: "privat" });
        // Ab hier liegt das Dokument sicher in der Cloud — ein Abbruch darf es
        // nicht mehr verschlucken.
        privatDokIdRef.current = dok.id;
        if (abgebrochenRef.current) return;
        privatDokIdRef.current = null;
        qc.invalidateQueries({ queryKey: ["privat"] });
        setGesichert({ art: "privat", bild: previewUrlFuer(seitenRef.current) });
        setSchritt("gesichert");
        return;
      }

      const isLieferschein = ctx.beleg_typ === "lieferschein";
      const beleg = await api.uploadBeleg(blobs, {
        app_version: APP_VERSION,
        ausgabe_typ: ctx.ausgabe_typ || undefined,
        beleg_typ: ctx.beleg_typ || undefined,
        projekt_id: ctx.projekt_id || undefined,
        projekt_ids: ctx.projekt_ids && ctx.projekt_ids.length ? JSON.stringify(ctx.projekt_ids) : undefined,
        helfer_id: ctx.helfer_id || undefined,
        is_bewirtung: ctx.is_bewirtung,
        capture_meta: captureMeta ? JSON.stringify(captureMeta) : undefined,
        // Clara liest auf dem Server im Hintergrund — die App wartet nicht darauf.
        auswerten: isLieferschein ? "positionen" : "beleg",
      });
      if (abgebrochenRef.current) {
        // Abgebrochen, während der Upload noch lief — den Entwurf gleich wieder weg.
        try {
          await api.deleteBeleg(beleg.id);
        } catch {
          /* best effort */
        }
        return;
      }
      savedBelegIdRef.current = null;
      // Die Liste soll den neuen Entwurf sofort zeigen (mit „Clara liest …").
      qc.invalidateQueries({ queryKey: ["belege-recent"] });
      setGesichert({ art: isLieferschein ? "lieferschein" : "beleg", bild: previewUrlFuer(seitenRef.current) });
      setSchritt("gesichert");
    } catch (e) {
      if (abgebrochenRef.current) return;
      const grund = (e as Error).message || "";
      melde(
        `Hochladen hat nicht geklappt${grund ? ` (${grund})` : ""}. Seiten und Auswahl sind noch da — einfach nochmal „Sichern“.`,
        9000,
      );
      setSchritt("zuordnen");
    }
  }

  async function abbrechenWaehrendVerarbeitung() {
    abgebrochenRef.current = true;
    const dokId = privatDokIdRef.current;
    if (dokId) {
      privatDokIdRef.current = null;
      nav(`/privat/${dokId}`, { replace: true });
      return;
    }
    const id = savedBelegIdRef.current;
    if (id) {
      savedBelegIdRef.current = null;
      try {
        await api.deleteBeleg(id);
      } catch {
        /* best effort */
      }
    }
    // Die Seiten bleiben — nachbessern und erneut sichern.
    setSchritt("zuordnen");
  }

  // Nach dem Sichern: alles für den nächsten Beleg zurücksetzen.
  function neuerScan() {
    seitenRef.current.forEach((s) => {
      URL.revokeObjectURL(s.vorschau);
      if (s.ergebnis) URL.revokeObjectURL(s.ergebnis.url);
    });
    seitenRef.current = [];
    setSeiten([]);
    setPdf(null);
    setIndex(0);
    setGesichert(null);
    setLetzteWahl(null);
    setSchritt("kamera");
  }

  if (schritt === "gesichert" && gesichert) {
    return (
      <div className="sc-gesichert">
        <div className="sc-gesichert-mitte">
          <div className="sc-gesichert-bild">
            {gesichert.bild ? <img src={gesichert.bild} alt="" /> : <span>PDF</span>}
            <span className="sc-gesichert-haken" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5 10 17 19 7" /></svg>
            </span>
          </div>
          <h1>Gesichert</h1>
          <p>
            {gesichert.art === "privat"
              ? "Clara liest jetzt Titel, Datum und Garantie. In etwa einer halben Minute steht alles bei deinen privaten Dokumenten."
              : gesichert.art === "lieferschein"
                ? "Clara liest jetzt Lieferant und Positionen. In etwa einer halben Minute steht alles in der Liste."
                : "Clara liest jetzt Lieferant, Datum und Betrag. In etwa einer halben Minute steht alles in der Liste — dort prüfst und schließt du den Beleg ab."}
          </p>
        </div>
        <div className="sc-gesichert-fuss">
          <button className="sc-gesichert-knopf is-primaer" onClick={neuerScan}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M4 8.5h3l1.6-2.4h6.8L17 8.5h3a1 1 0 0 1 1 1V18a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5a1 1 0 0 1 1-1z" />
              <circle cx="12" cy="13" r="3.6" />
            </svg>
            {gesichert.art === "privat" ? "Neues Dokument scannen" : "Neuen Beleg scannen"}
          </button>
          <button
            className="sc-gesichert-knopf"
            onClick={() => nav(gesichert.art === "privat" ? "/privat" : "/inbox", { replace: true })}
          >
            {gesichert.art === "privat" ? "Zu den privaten Dokumenten" : "Zur Liste"}
          </button>
        </div>
      </div>
    );
  }

  if (schritt === "verarbeiten") {
    return (
      <ProcessingScreen
        previewUrl={previewUrl}
        previewAspect={previewAspect}
        onAspect={setPreviewAspect}
        statusMsg={statusMsg}
        onCancel={abbrechenWaehrendVerarbeitung}
      />
    );
  }

  if (schritt === "zuordnen") {
    const erste = seiten[0];
    return (
      <>
        <Zuordnung
          anfangsKat={anfangsKat}
          vorher={letzteWahl}
          vorschauUrl={pdf ? null : erste?.ergebnis?.url ?? erste?.vorschau ?? null}
          umfang={pdf ? "PDF" : seiten.length === 1 ? "1 Seite" : `${seiten.length} Seiten`}
          onZurueck={() => {
            if (pdf) {
              setPdf(null);
              setSchritt("kamera");
            } else setSchritt("uebersicht");
          }}
          onSichern={verarbeiten}
        />
        {hinweis && <div className="sc-meldung">{hinweis}</div>}
      </>
    );
  }

  const editorSeite = editorId ? seiten.find((s) => s.id === editorId) : undefined;
  const letzte = seiten[seiten.length - 1];

  return (
    <div className="sc-ablauf">
      <ScanKamera
        aktiv={schritt === "kamera" && !verwerfenFrage}
        seitenAnzahl={seiten.length}
        stapelBild={letzte ? letzte.ergebnis?.url ?? letzte.vorschau : null}
        ersetzen={ersetzen}
        onSeite={neueSeite}
        onFertig={() => {
          setIndex(0);
          setSchritt("uebersicht");
        }}
        onSchliessen={() => (seiten.length ? setVerwerfenFrage(true) : nav("/inbox"))}
        onDateien={dateien}
        onVideo={(w, h) => (videoMassRef.current = { w, h })}
      />

      {schritt === "uebersicht" && seiten.length > 0 && (
        <ScanUebersicht
          seiten={seiten}
          index={index}
          onIndex={setIndex}
          onZurKamera={zurKamera}
          onAnpassen={(id) => setEditorId(id)}
          onFilter={(id, f) => aendere(id, { filter: f })}
          onDrehen={(id) => {
            const s = seitenRef.current.find((x) => x.id === id);
            if (s) aendere(id, { drehung: (((s.drehung + 90) % 360) as Drehung) });
          }}
          onWiederholen={(id) => {
            ersetzeRef.current = id;
            setErsetzen(true);
            setSchritt("kamera");
          }}
          onLoeschen={loesche}
          onWeiter={() => setSchritt("zuordnen")}
        />
      )}

      {editorSeite && (
        <EckenEditor
          bild={editorSeite.quelle}
          w={editorSeite.w}
          h={editorSeite.h}
          ecken={editorSeite.ecken}
          onAbbrechen={() => setEditorId(null)}
          onUebernehmen={(e) => {
            aendere(editorSeite.id, { ecken: e });
            setEditorId(null);
          }}
        />
      )}

      {verwerfenFrage && (
        <div className="sc-frage-hinter" onClick={() => setVerwerfenFrage(false)}>
          <div className="sc-frage" onClick={(e) => e.stopPropagation()}>
            <h3>{seiten.length === 1 ? "Gescannte Seite verwerfen?" : `${seiten.length} gescannte Seiten verwerfen?`}</h3>
            <p>Noch ist nichts hochgeladen.</p>
            <button className="sc-knopf sc-knopf-gefahr" onClick={verwerfen}>
              Verwerfen
            </button>
            <button className="sc-knopf" onClick={() => setVerwerfenFrage(false)}>
              Weiter scannen
            </button>
          </div>
        </div>
      )}

      {hinweis && <div className="sc-meldung">{hinweis}</div>}
    </div>
  );
}

/**
 * Hält einen eigenen Eintrag im Verlauf, solange ein Scan läuft. Die
 * Zurück-Geste verbraucht ihn; `schritt()` entscheidet, ob wir im Scan
 * bleiben (true → Eintrag neu setzen) oder die Seite verlassen (false).
 */
function useZurueckWache(aktiv: boolean, schritt: () => boolean) {
  const schrittRef = useRef(schritt);
  schrittRef.current = schritt;
  const gesetztRef = useRef(false);
  useEffect(() => {
    const auf = () => {
      if (!gesetztRef.current) return;
      gesetztRef.current = false;
      if (schrittRef.current()) {
        window.history.pushState({ vgScan: true }, "");
        gesetztRef.current = true;
      } else {
        window.history.back();
      }
    };
    window.addEventListener("popstate", auf);
    return () => window.removeEventListener("popstate", auf);
  }, []);
  useEffect(() => {
    if (aktiv && !gesetztRef.current) {
      window.history.pushState({ vgScan: true }, "");
      gesetztRef.current = true;
    }
  }, [aktiv]);
}

/** Kein Gummiband-Scrollen und kein Zoomen hinter der Kamera. */
function useKoerperSperre() {
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const vorher = [html.style.overflow, body.style.overflow, body.style.position, body.style.width, body.style.height];
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.width = "100%";
    body.style.height = "100%";
    return () => {
      [html.style.overflow, body.style.overflow, body.style.position, body.style.width, body.style.height] = vorher;
    };
  }, []);
}

/** Vorschau der ersten Seite für den „Gesichert"-Schirm (bleibt bis zum nächsten Scan gültig). */
function previewUrlFuer(liste: ScanSeite[]): string | null {
  const s = liste[0];
  return s ? s.ergebnis?.url ?? s.vorschau : null;
}

// ─────────────────────────── ProcessingScreen ─────────────────────────────

function ProcessingScreen({
  previewUrl,
  previewAspect,
  onAspect,
  statusMsg,
  onCancel,
}: {
  previewUrl: string | null;
  previewAspect: number | null;
  onAspect: (aspect: number) => void;
  statusMsg: string;
  onCancel: () => void;
}) {
  // Langer Beleg (Kassenbon): aspect (h/w) > 1.6 → Slide-Effekt von oben nach unten
  const isLongReceipt = previewAspect !== null && previewAspect > 1.6;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 40,
        background: "black",
        overflow: "hidden",
      }}
    >
      {/* Vorschaubild — fullwidth, bei langem Beleg langsamer Slide */}
      {previewUrl && (
        <motion.img
          key={previewUrl}
          src={previewUrl}
          alt="Beleg wird verarbeitet"
          onLoad={(e) => {
            const w = e.currentTarget.naturalWidth;
            const h = e.currentTarget.naturalHeight;
            if (w && h) onAspect(h / w);
          }}
          initial={isLongReceipt ? { y: "0%" } : false}
          animate={isLongReceipt ? { y: "-55%" } : {}}
          transition={
            isLongReceipt ? { duration: 25, ease: "linear" } : undefined
          }
          draggable={false}
          style={
            isLongReceipt
              ? {
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "auto",
                  userSelect: "none",
                  WebkitUserSelect: "none",
                }
              : {
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  userSelect: "none",
                  WebkitUserSelect: "none",
                }
          }
        />
      )}

      {/* Abbrechen-Knopf oben rechts */}
      <button
        onClick={onCancel}
        aria-label="Abbrechen"
        style={{
          position: "absolute",
          top: "calc(env(safe-area-inset-top) + 14px)",
          right: 14,
          zIndex: 10,
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "8px 14px",
          borderRadius: 999,
          border: "none",
          background: "rgba(0,0,0,0.6)",
          backdropFilter: "blur(6px)",
          color: "white",
          fontSize: 13,
          cursor: "pointer",
        }}
      >
        <X size={16} />
        Abbrechen
      </button>

      {/* Status-Overlay unten — dezenter Gradient + Loader */}
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          padding: "60px 24px calc(env(safe-area-inset-bottom) + 36px)",
          background:
            "linear-gradient(to top, rgba(0,0,0,0.92), rgba(0,0,0,0.55) 60%, transparent)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 10,
          textAlign: "center",
          pointerEvents: "none",
        }}
      >
        <Loader2
          size={32}
          style={{
            color: "white",
            animation: "spin 1.2s linear infinite",
          }}
        />
        <div style={{ color: "white", fontSize: 15, fontWeight: 600 }}>
          {statusMsg || "Beleg wird verarbeitet…"}
        </div>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
